import type {
  KeyChannelBinding,
  SetKeyChannelBindingsRequest,
  SetKeyChannelBindingsResponse,
} from '@nadoutong/contracts';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { listBindings, setBindings } from './keyChannelBindings.ts';
import { invalidateRoutingTargetDisplayNames } from './routingTargetDisplayNames.ts';

const BINDINGS_KEY = ['key-channel-bindings'] as const;

function bindingsQueryKey(
  upstreamId: string,
  bucketId: string,
): readonly ['key-channel-bindings', string, string] {
  return [...BINDINGS_KEY, upstreamId, bucketId] as const;
}

export function useKeyChannelBindingsList(
  upstreamId: string,
  bucketId: string,
): UseQueryResult<readonly KeyChannelBinding[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: bindingsQueryKey(upstreamId, bucketId),
    queryFn: async () => {
      const res = await listBindings(upstreamId, bucketId, fetchImpl);
      return res.bindings;
    },
    enabled: bucketId.length > 0,
  });
}

export interface KeyChannelBindingsIndex {
  /**
   * `bucket id → bindings`, or `undefined` when any single bucket failed.
   * A partial index under-reports which namespaces a key reaches, which
   * reads downstream as a missing prerequisite rather than a failed read.
   *
   * There is deliberately no `isPending` alongside this: an in-flight read
   * is already `undefined` here, and consumers must treat "loading" and
   * "failed" identically anyway — both mean "we cannot answer yet".
   */
  readonly byBucketId: ReadonlyMap<string, readonly KeyChannelBinding[]> | undefined;
}

/**
 * Bindings for many key buckets at once, for readers that need the whole
 * upstream rather than one bucket's row.
 *
 * Deliberately fans out over the same per-bucket query key as
 * `useKeyChannelBindingsList` instead of adding a bulk endpoint: the two
 * readers then share one cache entry per bucket, so a save in the
 * key-channel sheet — which invalidates exactly that key — refreshes both.
 */
export function useKeyChannelBindingsIndex(
  upstreamId: string,
  bucketIds: readonly string[],
): KeyChannelBindingsIndex {
  const { fetchImpl } = useSession();
  return useQueries({
    queries: bucketIds.map((bucketId) => ({
      queryKey: bindingsQueryKey(upstreamId, bucketId),
      queryFn: async (): Promise<readonly KeyChannelBinding[]> => {
        const res = await listBindings(upstreamId, bucketId, fetchImpl);
        return res.bindings;
      },
    })),
    combine: (results): KeyChannelBindingsIndex => {
      const byBucketId = new Map<string, readonly KeyChannelBinding[]>();
      let complete = true;
      results.forEach((result, index) => {
        const bucketId = bucketIds[index];
        if (bucketId === undefined) return;
        if (result.data === undefined) {
          complete = false;
          return;
        }
        byBucketId.set(bucketId, result.data);
      });
      return { byBucketId: complete ? byBucketId : undefined };
    },
  });
}

export function useSetKeyChannelBindings(
  upstreamId: string,
  bucketId: string,
): UseMutationResult<
  SetKeyChannelBindingsResponse,
  Error,
  SetKeyChannelBindingsRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetKeyChannelBindingsRequest) =>
      setBindings(upstreamId, bucketId, body, fetchImpl),
    onSuccess: () => {
      invalidateRoutingTargetDisplayNames(qc);
      void qc.invalidateQueries({ queryKey: bindingsQueryKey(upstreamId, bucketId) });
    },
  });
}
